import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useTheme,
  useMediaQuery,
  Box,
  Container,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: 'Home', path: '/' },
    { name: 'Games', path: '/games' },
    { name: 'Apps', path: '/apps' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ py: 2 }}>
      <Box sx={{ px: 3, pb: 2, mb: 2, borderBottom: '1px solid rgba(55, 65, 81, 0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RocketLaunchIcon sx={{ color: '#374151', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#1F2937' }}>
            InnoVate
          </Typography>
        </Box>
      </Box>
      <List>
        {menuItems.map((item) => (
          <ListItem
            component={Link}
            to={item.path}
            key={item.name}
            onClick={handleDrawerToggle}
            sx={{
              mx: 1,
              borderRadius: 2,
              color: location.pathname === item.path ? '#1F2937' : '#6B7280',
              backgroundColor: location.pathname === item.path ? 'rgba(55, 65, 81, 0.08)' : 'transparent',
              fontWeight: location.pathname === item.path ? 600 : 400,
              textDecoration: 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(55, 65, 81, 0.05)',
                color: '#1F2937',
              },
            }}
          >
            <ListItemText 
              primary={item.name} 
              primaryTypographyProps={{ 
                fontWeight: location.pathname === item.path ? 600 : 500,
                fontSize: '0.95rem'
              }} 
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{ 
          background: scrolled 
            ? 'rgba(255, 255, 255, 0.85)' 
            : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: scrolled ? '1px solid rgba(55, 65, 81, 0.1)' : '1px solid transparent',
          boxShadow: scrolled ? '0 4px 30px rgba(0, 0, 0, 0.05)' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ py: 1, px: { xs: 0, sm: 2 } }}>
            {isMobile && (
              <IconButton
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ 
                  mr: 2,
                  color: '#374151',
                  '&:hover': {
                    backgroundColor: 'rgba(55, 65, 81, 0.08)',
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                mr: 4,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 42,
                  height: 42,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                  boxShadow: '0 4px 14px rgba(55, 65, 81, 0.25)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 6px 20px rgba(55, 65, 81, 0.35)',
                  },
                }}
              >
                <RocketLaunchIcon sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 800,
                  fontSize: '1.35rem',
                  background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.5px',
                }}
              >
                InnoVate
              </Typography>
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                {menuItems.map((item) => (
                  <Button
                    component={Link}
                    to={item.path}
                    key={item.name}
                    sx={{
                      px: 2.5,
                      py: 1,
                      borderRadius: '10px',
                      color: location.pathname === item.path ? '#1F2937' : '#6B7280',
                      fontWeight: location.pathname === item.path ? 600 : 500,
                      fontSize: '0.9rem',
                      backgroundColor: location.pathname === item.path ? 'rgba(55, 65, 81, 0.08)' : 'transparent',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(55, 65, 81, 0.06)',
                        color: '#1F2937',
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        bottom: 6,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: location.pathname === item.path ? '20px' : '0px',
                        height: '3px',
                        borderRadius: '3px',
                        background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                        transition: 'width 0.2s ease',
                      },
                      '&:hover::before': {
                        width: '20px',
                      },
                    }}
                  >
                    {item.name}
                  </Button>
                ))}
              </Box>
            )}

            {/* CTA Button */}
            {!isMobile && (
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                sx={{
                  ml: 2,
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(55, 65, 81, 0.25)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                    boxShadow: '0 6px 20px rgba(55, 65, 81, 0.35)',
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                Get Started
              </Button>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            width: 280,
            borderRadius: '0 20px 20px 0',
            boxShadow: '4px 0 30px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Toolbar sx={{ mb: 1 }} />
    </>
  );
};

export default Navbar; 
